// biome-ignore-all lint/suspicious/noTemplateCurlyInString: Handlebars templates use ${...} syntax intentionally
/**
 * Notification Domain Seed Data
 *
 * Contains all seed data for the Notification Service (27):
 * - Notification Templates
 * - Template Categories
 * - Channel Configurations
 * - Notification Rules
 * - Notification Preferences
 * - Notification Triggers
 * - Notification Providers
 *
 * @see 27-notification-service/prisma/schema.prisma
 */

import { NotificationEntityType } from '../../enums/domain.enums';
import { ACTIVITY_TYPE_LENDER_MESSAGE, NotificationChannelAPI } from '../../enums/notification.enums';
import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// TEMPLATE IDs
// =============================================================================

export const TEMPLATE_IDS = {
  // Case notifications
  CASE_CREATED: seedId('tpl-case-created', 1),
  CASE_STATUS_CHANGED: seedId('tpl-case-status', 2),
  CASE_ASSIGNED: seedId('tpl-case-assigned', 3),
  BORROWER_MESSAGE: seedId('tpl-borrower-message', 13),
  BORROWER_QUOTE_ACCEPTED: seedId('tpl-borrower-quote-accepted', 14),
  LENDER_MESSAGE: seedId('tpl-lender-message', 26),

  // Document notifications
  DOCUMENT_UPLOADED: seedId('tpl-doc-uploaded', 4),
  DOCUMENT_REQUESTED: seedId('tpl-doc-requested', 5),
  DOCUMENT_APPROVED: seedId('tpl-doc-approved', 6),

  // User / Auth notifications
  WELCOME_EMAIL: seedId('tpl-welcome', 7),
  PASSWORD_RESET: seedId('tpl-pwd-reset', 8),
  INVITATION_SENT: seedId('tpl-invite', 9),
  PASSWORD_CHANGED: seedId('tpl-pwd-changed', 15),
  EMPLOYEE_INVITE_ACCEPTED: seedId('tpl-emp-invite-accepted', 16),
  CONSUMER_INVITED: seedId('tpl-consumer-invite', 17),
  CONSUMER_INVITE_ACCEPTED: seedId('tpl-consumer-invite-accepted', 18),
  MFA_ENABLED: seedId('tpl-mfa-enabled', 19),
  MFA_DISABLED: seedId('tpl-mfa-disabled', 20),
  NEW_DEVICE_LOGIN: seedId('tpl-new-device', 21),
  ACCOUNT_DEACTIVATED: seedId('tpl-account-deactivated', 22),

  // Domain Intelligence notifications (Issue #703)
  DOMAIN_MATCH_DETECTED: seedId('tpl-domain-match', 23),
  PARTNER_EXISTING_BUSINESS: seedId('tpl-partner-existing', 24),
  BUSINESS_OWNER_INVITED: seedId('tpl-biz-owner-invite', 25),

  // Loan notifications
  LOAN_APPLICATION_RECEIVED: seedId('tpl-loan-received', 10),
  LOAN_STATUS_UPDATE: seedId('tpl-loan-status', 11),
  LOAN_APPROVAL: seedId('tpl-loan-approved', 12),
} as const;

export type TemplateId = (typeof TEMPLATE_IDS)[keyof typeof TEMPLATE_IDS];

// =============================================================================
// TEMPLATE CATEGORIES
// =============================================================================

export const TEMPLATE_CATEGORIES = {
  CASE: 'case',
  DOCUMENT: 'document',
  USER: 'user',
  AUTH: 'auth',
  LOAN: 'loan',
  SYSTEM: 'system',
} as const;

// =============================================================================
// NOTIFICATION RULE IDs
// =============================================================================

export const NOTIFICATION_RULE_IDS = {
  // System-wide rules
  SYSTEM_CRITICAL_ALERT: seedId('rule-sys-critical', 1),
  SYSTEM_MAINTENANCE: seedId('rule-sys-maintenance', 2),

  // Business-level rules
  CASE_ASSIGNED_RULE: seedId('rule-case-assigned', 1),
  LOAN_STATUS_CHANGE_RULE: seedId('rule-loan-status', 2),
  DOCUMENT_DEADLINE_RULE: seedId('rule-doc-deadline', 3),
  PAYMENT_RECEIVED_RULE: seedId('rule-payment-received', 4),
  SLA_BREACH_RULE: seedId('rule-sla-breach', 5),
} as const;

export type NotificationRuleId = (typeof NOTIFICATION_RULE_IDS)[keyof typeof NOTIFICATION_RULE_IDS];

// =============================================================================
// NOTIFICATION PREFERENCE IDs
// =============================================================================

export const NOTIFICATION_PREFERENCE_IDS = {
  PREF_RACHEL: seedId('pref-rachel', 1),
  PREF_MIKE: seedId('pref-mike', 2),
  PREF_SARAH: seedId('pref-sarah', 3),
  PREF_SUPER_ADMIN: seedId('pref-superadmin', 4),
} as const;

export type NotificationPreferenceId = (typeof NOTIFICATION_PREFERENCE_IDS)[keyof typeof NOTIFICATION_PREFERENCE_IDS];

// =============================================================================
// NOTIFICATION TRIGGER IDs
// =============================================================================

export const NOTIFICATION_TRIGGER_IDS = {
  // Case triggers
  TRIGGER_CASE_CREATED: seedId('trigger-case-created', 1),
  TRIGGER_CASE_ASSIGNED: seedId('trigger-case-assigned', 2),
  TRIGGER_CASE_STATUS_CHANGED: seedId('trigger-case-status', 3),
  TRIGGER_BORROWER_MESSAGE: seedId('trigger-borrower-message', 4),
  TRIGGER_BORROWER_QUOTE_ACCEPTED: seedId('trigger-borrower-quote-accepted', 5),
  TRIGGER_LENDER_MESSAGE: seedId('trigger-lender-message', 6),

  // Loan triggers
  TRIGGER_LOAN_SUBMITTED: seedId('trigger-loan-submitted', 1),
  TRIGGER_LOAN_APPROVED: seedId('trigger-loan-approved', 2),
  TRIGGER_LOAN_REJECTED: seedId('trigger-loan-rejected', 3),

  // Document triggers
  TRIGGER_DOC_REQUESTED: seedId('trigger-doc-requested', 1),
  TRIGGER_DOC_UPLOADED: seedId('trigger-doc-uploaded', 2),
  TRIGGER_DOC_EXPIRING: seedId('trigger-doc-expiring', 3),

  // Auth / User triggers
  TRIGGER_WELCOME_EMAIL: seedId('trigger-welcome-email', 1),
  TRIGGER_PASSWORD_CHANGED: seedId('trigger-pwd-changed', 2),
  TRIGGER_EMPLOYEE_INVITE_ACCEPTED: seedId('trigger-emp-invite-accepted', 3),
  TRIGGER_CONSUMER_INVITED: seedId('trigger-consumer-invited', 4),
  TRIGGER_MFA_ENABLED: seedId('trigger-mfa-enabled', 5),
  TRIGGER_MFA_DISABLED: seedId('trigger-mfa-disabled', 6),
  TRIGGER_NEW_DEVICE_LOGIN: seedId('trigger-new-device', 7),
  TRIGGER_ACCOUNT_DEACTIVATED: seedId('trigger-acct-deactivated', 8),

  // Domain Intelligence triggers (Issue #703)
  TRIGGER_DOMAIN_MATCH_DETECTED: seedId('trigger-domain-match', 9),
  TRIGGER_PARTNER_EXISTING_BUSINESS: seedId('trigger-partner-existing', 10),
  TRIGGER_BUSINESS_OWNER_INVITED: seedId('trigger-biz-owner-invite', 11),
} as const;

export type NotificationTriggerId = (typeof NOTIFICATION_TRIGGER_IDS)[keyof typeof NOTIFICATION_TRIGGER_IDS];

// =============================================================================
// NOTIFICATION PROVIDER IDs
// =============================================================================

export const NOTIFICATION_PROVIDER_IDS = {
  // OpsMatrix Capital providers
  OPSMATRIX_EMAIL_RESEND: seedId('provider-opsmatrix-email', 1),
  OPSMATRIX_SMS_TWILIO: seedId('provider-opsmatrix-sms', 2),
  OPSMATRIX_PUSH_FIREBASE: seedId('provider-opsmatrix-push', 3),

  // 1Sharpe Capital providers
  SHARPE_EMAIL_RESEND: seedId('provider-sharpe-email', 1),
} as const;

export type NotificationProviderId = (typeof NOTIFICATION_PROVIDER_IDS)[keyof typeof NOTIFICATION_PROVIDER_IDS];

// =============================================================================
// NOTIFICATION TEMPLATE DATA
// =============================================================================

export interface TemplateChannelSeedData {
  channel: NotificationChannelAPI;
  title?: string;
  subject?: string;
  body: string;
  htmlBody?: string;
}

export interface TemplateVariableSeedData {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date';
  required: boolean;
}

export interface NotificationTemplateSeedData {
  id: string;
  businessId: string;
  name: string;
  description: string;
  category: string;
  variables: TemplateVariableSeedData[];
  channels: TemplateChannelSeedData[];
  active: boolean;
}

export const NOTIFICATION_TEMPLATES: Record<keyof typeof TEMPLATE_IDS, NotificationTemplateSeedData> = {
  // -------------------------------------------------------------------------
  // Case Notifications
  // -------------------------------------------------------------------------
  CASE_CREATED: {
    id: TEMPLATE_IDS.CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Created',
    description: 'Notification sent when a new case is created',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'caseTitle', type: 'string', required: true },
      { name: 'consumerName', type: 'string', required: true },
      { name: 'createdAt', type: 'date', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'New Case Created: {{caseNumber}}',
        body: 'A new case "{{caseTitle}}" has been created for {{consumerName}}.',
        htmlBody: '<p>A new case <strong>{{caseTitle}}</strong> has been created for {{consumerName}}.</p>',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New Case',
        body: 'Case {{caseNumber}}: {{caseTitle}} created',
      },
    ],
    active: true,
  },

  CASE_STATUS_CHANGED: {
    id: TEMPLATE_IDS.CASE_STATUS_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Status Changed',
    description: 'Notification sent when a case status is updated',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'oldStatus', type: 'string', required: true },
      { name: 'newStatus', type: 'string', required: true },
      { name: 'updatedBy', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Case {{caseNumber}} Status Update',
        body: 'Case {{caseNumber}} status changed from {{oldStatus}} to {{newStatus}}.',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Status Update',
        body: 'Case {{caseNumber}}: {{oldStatus}} → {{newStatus}}',
      },
    ],
    active: true,
  },

  CASE_ASSIGNED: {
    id: TEMPLATE_IDS.CASE_ASSIGNED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Assigned',
    description: 'Notification sent when a case is assigned to a team member',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'caseTitle', type: 'string', required: true },
      { name: 'assigneeName', type: 'string', required: true },
      { name: 'assignedBy', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Case Assigned: {{caseNumber}}',
        body: 'You have been assigned to case {{caseNumber}}: {{caseTitle}}.',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Case Assigned',
        body: "You've been assigned to {{caseNumber}}",
      },
    ],
    active: true,
  },

  BORROWER_MESSAGE: {
    id: TEMPLATE_IDS.BORROWER_MESSAGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Borrower Message',
    description: 'Notification sent to case assignee when borrower sends a message',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'borrowerName', type: 'string', required: true },
      { name: 'messageContent', type: 'string', required: true },
      { name: 'caseUrl', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'New message from {{borrowerName}} on case {{caseNumber}}',
        body: '{{borrowerName}} sent a message on case {{caseNumber}}:\n\n"{{messageContent}}"\n\nView the case to respond.',
        htmlBody:
          '<p><strong>{{borrowerName}}</strong> sent a message on case <strong>{{caseNumber}}</strong>:</p><blockquote>{{messageContent}}</blockquote><p><a href="{{caseUrl}}">View Case</a></p>',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Message from {{borrowerName}}',
        body: '{{messageContent}}',
      },
      {
        channel: NotificationChannelAPI.PUSH,
        title: 'New message from {{borrowerName}}',
        body: '{{messageContent}}',
      },
    ],
    active: true,
  },

  BORROWER_QUOTE_ACCEPTED: {
    id: TEMPLATE_IDS.BORROWER_QUOTE_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Borrower Quote Accepted',
    description: 'Notification sent to case assignee when borrower accepts a quote',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'borrowerName', type: 'string', required: true },
      { name: 'lenderName', type: 'string', required: false },
      { name: 'loanAmount', type: 'number', required: false },
      { name: 'interestRate', type: 'number', required: false },
      { name: 'caseUrl', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: '🎉 Quote Accepted: {{borrowerName}} on case {{caseNumber}}',
        body: 'Great news! {{borrowerName}} has accepted a quote on case {{caseNumber}}.\n\nLender: {{lenderName}}\nAmount: ${{loanAmount}}\nRate: {{interestRate}}%\n\nView the case to proceed with next steps.',
        htmlBody:
          '<h2>🎉 Quote Accepted!</h2><p><strong>{{borrowerName}}</strong> has accepted a quote on case <strong>{{caseNumber}}</strong>.</p><table><tr><td>Lender:</td><td>{{lenderName}}</td></tr><tr><td>Amount:</td><td>${{loanAmount}}</td></tr><tr><td>Rate:</td><td>{{interestRate}}%</td></tr></table><p><a href="{{caseUrl}}">View Case</a></p>',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Quote Accepted! 🎉',
        body: '{{borrowerName}} accepted a quote on {{caseNumber}}',
      },
      {
        channel: NotificationChannelAPI.PUSH,
        title: '🎉 Quote Accepted',
        body: '{{borrowerName}} accepted a quote on {{caseNumber}}',
      },
    ],
    active: true,
  },

  LENDER_MESSAGE: {
    id: TEMPLATE_IDS.LENDER_MESSAGE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Lender Message',
    description: 'Notification sent to borrower when lender sends a message on their loan',
    category: TEMPLATE_CATEGORIES.CASE,
    variables: [
      { name: 'caseNumber', type: 'string', required: true },
      { name: 'lenderName', type: 'string', required: true },
      { name: 'messagePreview', type: 'string', required: true },
      { name: 'portalUrl', type: 'string', required: false },
      { name: 'borrowerName', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'New message on your loan application {{caseNumber}}',
        body: 'Hi {{borrowerName}},\n\n{{lenderName}} sent you a new message regarding your loan application {{caseNumber}}.\n\n"{{messagePreview}}"\n\nPlease log in to your portal to view the full message and respond.\n\n{{portalUrl}}',
        htmlBody: [
          '<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;color:#333">',
          '<div style="background:#4F46E5;padding:24px 32px;border-radius:8px 8px 0 0">',
          '<h1 style="color:#fff;margin:0;font-size:20px">New Message on Your Loan</h1>',
          '</div>',
          '<div style="padding:24px 32px;background:#fff;border:1px solid #E5E7EB;border-top:none">',
          '<p style="margin:0 0 16px">Hi {{borrowerName}},</p>',
          '<p style="margin:0 0 16px"><strong>{{lenderName}}</strong> sent you a message on loan application <strong>{{caseNumber}}</strong>:</p>',
          '<div style="background:#F3F4F6;border-left:4px solid #4F46E5;padding:12px 16px;margin:0 0 20px;border-radius:0 6px 6px 0">',
          '<p style="margin:0;color:#374151;font-style:italic">&ldquo;{{messagePreview}}&rdquo;</p>',
          '</div>',
          '<a href="{{portalUrl}}" style="display:inline-block;background:#4F46E5;color:#fff;padding:10px 24px;border-radius:6px;text-decoration:none;font-weight:600">View Full Message &amp; Reply</a>',
          '</div>',
          '<div style="padding:16px 32px;text-align:center;color:#9CA3AF;font-size:12px;border:1px solid #E5E7EB;border-top:none;border-radius:0 0 8px 8px">',
          '<p style="margin:0">You received this because you have an active loan application. To manage notifications, visit your portal settings.</p>',
          '</div>',
          '</div>',
        ].join(''),
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New message from {{lenderName}}',
        body: 'Loan {{caseNumber}}: "{{messagePreview}}"',
      },
      {
        channel: NotificationChannelAPI.PUSH,
        title: 'New message on Loan {{caseNumber}}',
        body: '{{lenderName}}: "{{messagePreview}}"',
      },
    ],
    active: true,
  },

  // -------------------------------------------------------------------------
  // Document Notifications
  // -------------------------------------------------------------------------
  DOCUMENT_UPLOADED: {
    id: TEMPLATE_IDS.DOCUMENT_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Uploaded',
    description: 'Notification sent when a document is uploaded',
    category: TEMPLATE_CATEGORIES.DOCUMENT,
    variables: [
      { name: 'documentName', type: 'string', required: true },
      { name: 'documentType', type: 'string', required: true },
      { name: 'uploadedBy', type: 'string', required: true },
      { name: 'caseNumber', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Document Uploaded: {{documentName}}',
        body: '{{uploadedBy}} uploaded a new {{documentType}} document: {{documentName}}.',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Document Uploaded',
        body: '{{documentName}} uploaded by {{uploadedBy}}',
      },
    ],
    active: true,
  },

  DOCUMENT_REQUESTED: {
    id: TEMPLATE_IDS.DOCUMENT_REQUESTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Requested',
    description: 'Notification sent when a document is requested from a consumer',
    category: TEMPLATE_CATEGORIES.DOCUMENT,
    variables: [
      { name: 'documentType', type: 'string', required: true },
      { name: 'requestedBy', type: 'string', required: true },
      { name: 'dueDate', type: 'date', required: false },
      { name: 'caseNumber', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Document Request: {{documentType}}',
        body: 'Please upload the following document: {{documentType}}. {{#if dueDate}}Due by: {{dueDate}}{{/if}}',
        htmlBody:
          '<p>Please upload the following document: <strong>{{documentType}}</strong>.</p>{{#if dueDate}}<p>Due by: {{dueDate}}</p>{{/if}}',
      },
      {
        channel: NotificationChannelAPI.SMS,
        body: 'Document requested: {{documentType}}. Please log in to upload.',
      },
    ],
    active: true,
  },

  DOCUMENT_APPROVED: {
    id: TEMPLATE_IDS.DOCUMENT_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Approved',
    description: 'Notification sent when a document is approved',
    category: TEMPLATE_CATEGORIES.DOCUMENT,
    variables: [
      { name: 'documentName', type: 'string', required: true },
      { name: 'approvedBy', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Document Approved: {{documentName}}',
        body: 'Your document {{documentName}} has been approved by {{approvedBy}}.',
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'Document Approved',
        body: '{{documentName}} approved ✓',
      },
    ],
    active: true,
  },

  // -------------------------------------------------------------------------
  // User Notifications
  // -------------------------------------------------------------------------
  WELCOME_EMAIL: {
    id: TEMPLATE_IDS.WELCOME_EMAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Welcome Email',
    description: 'Welcome email sent to new users',
    category: TEMPLATE_CATEGORIES.USER,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
      { name: 'loginUrl', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Welcome to {{businessName}}!',
        body: 'Hi {{userName}}, welcome to {{businessName}}! Get started at {{loginUrl}}.',
        htmlBody: [
          '<h1>Welcome, {{userName}}!</h1>',
          "<p>You've been added to <strong>{{businessName}}</strong>. Your account is ready to use.</p>",
          '<p><a class="btn" href="{{loginUrl}}">Log In &amp; Get Started</a></p>',
          "<p>If you have any questions, don't hesitate to reach out to your administrator.</p>",
        ].join(''),
      },
    ],
    active: true,
  },

  PASSWORD_RESET: {
    id: TEMPLATE_IDS.PASSWORD_RESET,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Password Reset',
    description: 'Password reset email',
    category: TEMPLATE_CATEGORIES.USER,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'resetUrl', type: 'string', required: true },
      { name: 'expiresIn', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Reset your password',
        body: 'Hi {{userName}}, click this link to reset your password: {{resetUrl}}. Expires in {{expiresIn}}.',
        htmlBody: [
          '<h2>Password Reset</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>We received a request to reset your password. Click the button below to choose a new one.</p>',
          '<p><a class="btn" href="{{resetUrl}}">Reset Password</a></p>',
          "<p>This link expires in <strong>{{expiresIn}}</strong>. If you didn't request this, you can safely ignore this email.</p>",
          '<div class="alert-box">If you did not request a password reset, your account may have been compromised. Please contact support immediately.</div>',
        ].join(''),
      },
    ],
    active: true,
  },

  INVITATION_SENT: {
    id: TEMPLATE_IDS.INVITATION_SENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Invitation Sent',
    description: 'Email sent when inviting a new team member',
    category: TEMPLATE_CATEGORIES.USER,
    variables: [
      { name: 'inviteeName', type: 'string', required: true },
      { name: 'inviterName', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
      { name: 'inviteUrl', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: "You've been invited to join {{businessName}}",
        body: 'Hi {{inviteeName}}, {{inviterName}} has invited you to join {{businessName}}. Accept: {{inviteUrl}}',
        htmlBody: [
          "<h2>You're Invited!</h2>",
          '<p>Hi {{inviteeName}},</p>',
          '<p><strong>{{inviterName}}</strong> has invited you to join <strong>{{businessName}}</strong>.</p>',
          '<p><a class="btn" href="{{inviteUrl}}">Accept Invitation</a></p>',
          "<p>This invitation was sent to you because a team member at {{businessName}} added you. If you weren't expecting this, you can ignore it.</p>",
        ].join(''),
      },
    ],
    active: true,
  },

  // -------------------------------------------------------------------------
  // Auth / Security Notifications (Issue #699)
  // -------------------------------------------------------------------------
  PASSWORD_CHANGED: {
    id: TEMPLATE_IDS.PASSWORD_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Password Changed',
    description: 'Security alert when a user password is changed',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'changedAt', type: 'date', required: true },
      { name: 'loginUrl', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Your password has been changed',
        body: 'Hi {{userName}}, your password was successfully changed on {{changedAt}}. If you did not make this change, please contact support immediately.',
        htmlBody: [
          '<h2>Password Changed</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>Your password was successfully changed on <strong>{{changedAt}}</strong>.</p>',
          '<div class="alert-box">If you did not make this change, please reset your password immediately and contact your administrator.</div>',
        ].join(''),
      },
    ],
    active: true,
  },

  EMPLOYEE_INVITE_ACCEPTED: {
    id: TEMPLATE_IDS.EMPLOYEE_INVITE_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Employee Invite Accepted',
    description: 'Notification when an employee accepts their team invitation',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'employeeName', type: 'string', required: true },
      { name: 'employeeEmail', type: 'string', required: true },
      { name: 'role', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: '{{employeeName}} has joined {{businessName}}',
        body: '{{employeeName}} ({{employeeEmail}}) accepted the invitation and joined {{businessName}} as {{role}}.',
        htmlBody: [
          '<h2>New Team Member</h2>',
          '<p><strong>{{employeeName}}</strong> has accepted the invitation and joined your team.</p>',
          '<table class="detail-table"><tbody>',
          '<tr><td>Name</td><td>{{employeeName}}</td></tr>',
          '<tr><td>Email</td><td>{{employeeEmail}}</td></tr>',
          '<tr><td>Role</td><td>{{role}}</td></tr>',
          '<tr><td>Organization</td><td>{{businessName}}</td></tr>',
          '</tbody></table>',
          '<div class="info-box">This team member now has access to the platform based on their assigned role.</div>',
        ].join(''),
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New team member',
        body: '{{employeeName}} joined as {{role}}',
      },
    ],
    active: true,
  },

  CONSUMER_INVITED: {
    id: TEMPLATE_IDS.CONSUMER_INVITED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Consumer Invitation',
    description: 'Email sent when a business invites a consumer to their portal',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'consumerName', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
      { name: 'inviteUrl', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: "You've been invited to {{businessName}}",
        body: 'Hi {{consumerName}}, {{businessName}} has invited you to their portal. Get started: {{inviteUrl}}',
        htmlBody: [
          "<h2>You're Invited</h2>",
          '<p>Hi {{consumerName}},</p>',
          '<p><strong>{{businessName}}</strong> has invited you to their client portal where you can securely manage your account, track progress, and communicate with your team.</p>',
          '<p><a class="btn" href="{{inviteUrl}}">Accept Invitation</a></p>',
          "<p>If you weren't expecting this invitation, you can safely ignore this email.</p>",
        ].join(''),
      },
    ],
    active: true,
  },

  CONSUMER_INVITE_ACCEPTED: {
    id: TEMPLATE_IDS.CONSUMER_INVITE_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Consumer Invite Accepted',
    description: 'Notification when a consumer accepts their portal invitation',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'consumerName', type: 'string', required: true },
      { name: 'consumerEmail', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: '{{consumerName}} has joined your portal',
        body: '{{consumerName}} ({{consumerEmail}}) accepted the invitation to {{businessName}}.',
        htmlBody: [
          '<h2>New Client</h2>',
          '<p>A consumer has accepted your invitation and joined your portal.</p>',
          '<table class="detail-table"><tbody>',
          '<tr><td>Name</td><td>{{consumerName}}</td></tr>',
          '<tr><td>Email</td><td>{{consumerEmail}}</td></tr>',
          '<tr><td>Organization</td><td>{{businessName}}</td></tr>',
          '</tbody></table>',
          '<div class="info-box">This client can now access your consumer portal and submit documents.</div>',
        ].join(''),
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New consumer',
        body: '{{consumerName}} joined your portal',
      },
    ],
    active: true,
  },

  MFA_ENABLED: {
    id: TEMPLATE_IDS.MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'MFA Enabled',
    description: 'Confirmation when MFA is enabled on an account',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'enabledAt', type: 'date', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Two-factor authentication enabled',
        body: 'Hi {{userName}}, two-factor authentication was enabled on your account on {{enabledAt}}. Your account is now more secure.',
        htmlBody: [
          '<h2>Two-Factor Authentication Enabled</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>Two-factor authentication (2FA) was successfully enabled on your account on <strong>{{enabledAt}}</strong>.</p>',
          '<div class="info-box">Your account is now more secure. You\'ll be asked for a verification code each time you sign in from a new device.</div>',
          '<p>Make sure to keep your backup codes in a safe place in case you lose access to your authenticator app.</p>',
        ].join(''),
      },
    ],
    active: true,
  },

  MFA_DISABLED: {
    id: TEMPLATE_IDS.MFA_DISABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'MFA Disabled',
    description: 'Security alert when MFA is disabled on an account',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'disabledAt', type: 'date', required: true },
      { name: 'disabledBy', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Two-factor authentication disabled',
        body: 'Hi {{userName}}, two-factor authentication was disabled on your account on {{disabledAt}}.{{#if disabledBy}} This was done by {{disabledBy}}.{{/if}} If you did not make this change, please contact support immediately.',
        htmlBody: [
          '<h2>Two-Factor Authentication Disabled</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>Two-factor authentication (2FA) was <strong>disabled</strong> on your account on <strong>{{disabledAt}}</strong>.',
          '{{#if disabledBy}} This action was performed by <strong>{{disabledBy}}</strong>.{{/if}}</p>',
          '<div class="alert-box">Your account is now less secure. If you did not make this change, please re-enable 2FA immediately and contact your administrator.</div>',
        ].join(''),
      },
    ],
    active: true,
  },

  NEW_DEVICE_LOGIN: {
    id: TEMPLATE_IDS.NEW_DEVICE_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New Device Login',
    description: 'Security alert when a login occurs from a new device',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'deviceName', type: 'string', required: false },
      { name: 'browser', type: 'string', required: false },
      { name: 'os', type: 'string', required: false },
      { name: 'ipAddress', type: 'string', required: false },
      { name: 'loginAt', type: 'date', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'New sign-in to your account',
        body: 'Hi {{userName}}, a new sign-in to your account was detected on {{loginAt}}.{{#if deviceName}} Device: {{deviceName}}.{{/if}}{{#if browser}} Browser: {{browser}}.{{/if}} If this was not you, please change your password immediately.',
        htmlBody: [
          '<h2>New Sign-In Detected</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>We noticed a new sign-in to your account:</p>',
          '<table class="detail-table"><tbody>',
          '<tr><td>Time</td><td>{{loginAt}}</td></tr>',
          '{{#if deviceName}}<tr><td>Device</td><td>{{deviceName}}</td></tr>{{/if}}',
          '{{#if browser}}<tr><td>Browser</td><td>{{browser}}</td></tr>{{/if}}',
          '{{#if os}}<tr><td>OS</td><td>{{os}}</td></tr>{{/if}}',
          '{{#if ipAddress}}<tr><td>IP Address</td><td>{{ipAddress}}</td></tr>{{/if}}',
          '</tbody></table>',
          '<div class="alert-box">If this wasn\'t you, please change your password immediately and review your account activity.</div>',
        ].join(''),
      },
    ],
    active: true,
  },

  ACCOUNT_DEACTIVATED: {
    id: TEMPLATE_IDS.ACCOUNT_DEACTIVATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Account Deactivated',
    description: 'Notification when a user account is deactivated',
    category: TEMPLATE_CATEGORIES.AUTH,
    variables: [
      { name: 'userName', type: 'string', required: true },
      { name: 'businessName', type: 'string', required: true },
      { name: 'deactivatedBy', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Your account has been deactivated',
        body: 'Hi {{userName}}, your account at {{businessName}} has been deactivated.{{#if deactivatedBy}} This was done by {{deactivatedBy}}.{{/if}} If you believe this is an error, please contact your administrator.',
        htmlBody: [
          '<h2>Account Deactivated</h2>',
          '<p>Hi {{userName}},</p>',
          '<p>Your account at <strong>{{businessName}}</strong> has been deactivated.',
          '{{#if deactivatedBy}} This action was performed by <strong>{{deactivatedBy}}</strong>.{{/if}}</p>',
          '<p>You will no longer be able to sign in or access the platform until your account is reactivated.</p>',
          '<p>If you believe this is an error, please contact your administrator.</p>',
        ].join(''),
      },
    ],
    active: true,
  },

  // -------------------------------------------------------------------------
  // Domain Intelligence Notifications (Issue #703)
  // -------------------------------------------------------------------------
  DOMAIN_MATCH_DETECTED: {
    id: TEMPLATE_IDS.DOMAIN_MATCH_DETECTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Domain Match Detected',
    description: 'Alert sent to existing business admins when someone from their email domain attempts signup',
    category: TEMPLATE_CATEGORIES.SYSTEM,
    variables: [
      { name: 'newUserEmail', type: 'string', required: true },
      { name: 'newUserName', type: 'string', required: false },
      { name: 'businessName', type: 'string', required: true },
      { name: 'teamSettingsUrl', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'New signup from your domain',
        body: 'Someone ({{newUserEmail}}) attempted to create a new account. Their email domain matches your company ({{businessName}}). You can invite them from Settings > Team Members.',
        htmlBody: [
          '<h2>New Signup From Your Domain</h2>',
          '<p><strong>{{newUserEmail}}</strong>{{#if newUserName}} ({{newUserName}}){{/if}} attempted to create a new account on the platform.</p>',
          '<p>Their email domain matches your company: <strong>{{businessName}}</strong>.</p>',
          '{{#if teamSettingsUrl}}<p><a class="btn" href="{{teamSettingsUrl}}">Invite This User</a></p>{{/if}}',
          '<p>If you do not recognize this person, no action is needed.</p>',
        ].join(''),
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New signup from your domain',
        body: '{{newUserEmail}} attempted to create a new account matching {{businessName}}',
      },
    ],
    active: true,
  },

  PARTNER_EXISTING_BUSINESS: {
    id: TEMPLATE_IDS.PARTNER_EXISTING_BUSINESS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Partner Linked to Existing Business',
    description: 'Notification sent to B2 admin when a partner auto-links to their existing business',
    category: TEMPLATE_CATEGORIES.SYSTEM,
    variables: [
      { name: 'invitingCompany', type: 'string', required: true },
      { name: 'inviterName', type: 'string', required: false },
      { name: 'partnerType', type: 'string', required: false },
      { name: 'businessName', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: '{{invitingCompany}} has partnered with your company',
        body: '{{#if inviterName}}{{inviterName}} from {{/if}}{{invitingCompany}} has established a {{#if partnerType}}{{partnerType}} {{/if}}partnership with {{businessName}} through the platform.',
        htmlBody: [
          '<h2>New Partnership</h2>',
          '<p>{{#if inviterName}}<strong>{{inviterName}}</strong> from {{/if}}<strong>{{invitingCompany}}</strong> has established a{{#if partnerType}} {{partnerType}}{{/if}} partnership with <strong>{{businessName}}</strong> through the platform.</p>',
          '<p>You can manage your partnerships from the Partner Management section of your portal.</p>',
        ].join(''),
      },
      {
        channel: NotificationChannelAPI.IN_APP,
        title: 'New partnership',
        body: '{{invitingCompany}} has partnered with {{businessName}}',
      },
    ],
    active: true,
  },

  BUSINESS_OWNER_INVITED: {
    id: TEMPLATE_IDS.BUSINESS_OWNER_INVITED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Business Owner Invited',
    description: 'Invitation sent to business owner when a platform admin creates their business account',
    category: TEMPLATE_CATEGORIES.USER,
    variables: [
      { name: 'businessName', type: 'string', required: true },
      { name: 'inviteUrl', type: 'string', required: true },
      { name: 'adminName', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Your business account is ready',
        body: '{{#if adminName}}{{adminName}} has created{{else}}A platform administrator has created{{/if}} a business account for {{businessName}}. Click the link to complete your profile and activate your account: {{inviteUrl}}',
        htmlBody: [
          '<h2>Your Business Account Is Ready</h2>',
          '<p>{{#if adminName}}<strong>{{adminName}}</strong> has created{{else}}A platform administrator has created{{/if}} a business account for <strong>{{businessName}}</strong>.</p>',
          '<p>Click the button below to complete your profile and activate your account.</p>',
          '<p><a class="btn" href="{{inviteUrl}}">Activate Your Account</a></p>',
          "<p>If you weren't expecting this invitation, you can safely ignore this email.</p>",
        ].join(''),
      },
    ],
    active: true,
  },

  // -------------------------------------------------------------------------
  // Loan Notifications
  // -------------------------------------------------------------------------
  LOAN_APPLICATION_RECEIVED: {
    id: TEMPLATE_IDS.LOAN_APPLICATION_RECEIVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Application Received',
    description: 'Confirmation when a loan application is submitted',
    category: TEMPLATE_CATEGORIES.LOAN,
    variables: [
      { name: 'applicantName', type: 'string', required: true },
      { name: 'applicationId', type: 'string', required: true },
      { name: 'loanAmount', type: 'number', required: true },
      { name: 'loanType', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Application Received: {{applicationId}}',
        body: 'Thank you {{applicantName}}, we received your {{loanType}} application for ${{loanAmount}}. Reference: {{applicationId}}.',
        htmlBody:
          '<h2>Application Received</h2><p>Thank you {{applicantName}}, we have received your {{loanType}} loan application.</p><ul><li>Amount: ${{loanAmount}}</li><li>Reference: {{applicationId}}</li></ul>',
      },
      {
        channel: NotificationChannelAPI.SMS,
        body: "Application {{applicationId}} received. We'll be in touch soon.",
      },
    ],
    active: true,
  },

  LOAN_STATUS_UPDATE: {
    id: TEMPLATE_IDS.LOAN_STATUS_UPDATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Status Update',
    description: 'Notification when loan status changes',
    category: TEMPLATE_CATEGORIES.LOAN,
    variables: [
      { name: 'applicantName', type: 'string', required: true },
      { name: 'applicationId', type: 'string', required: true },
      { name: 'newStatus', type: 'string', required: true },
      { name: 'statusMessage', type: 'string', required: false },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Loan Update: {{applicationId}}',
        body: 'Hi {{applicantName}}, your loan application {{applicationId}} status is now: {{newStatus}}. {{statusMessage}}',
      },
      {
        channel: NotificationChannelAPI.PUSH,
        title: 'Loan Update',
        body: '{{applicationId}}: {{newStatus}}',
      },
    ],
    active: true,
  },

  LOAN_APPROVAL: {
    id: TEMPLATE_IDS.LOAN_APPROVAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Approval',
    description: 'Notification when a loan is approved',
    category: TEMPLATE_CATEGORIES.LOAN,
    variables: [
      { name: 'applicantName', type: 'string', required: true },
      { name: 'applicationId', type: 'string', required: true },
      { name: 'approvedAmount', type: 'number', required: true },
      { name: 'interestRate', type: 'number', required: true },
      { name: 'term', type: 'string', required: true },
    ],
    channels: [
      {
        channel: NotificationChannelAPI.EMAIL,
        subject: 'Congratulations! Loan Approved: {{applicationId}}',
        body: 'Great news {{applicantName}}! Your loan has been approved for ${{approvedAmount}} at {{interestRate}}% for {{term}}.',
        htmlBody:
          '<h1>🎉 Congratulations!</h1><p>Great news {{applicantName}}! Your loan application has been approved.</p><table><tr><td>Amount:</td><td>${{approvedAmount}}</td></tr><tr><td>Rate:</td><td>{{interestRate}}%</td></tr><tr><td>Term:</td><td>{{term}}</td></tr></table>',
      },
      {
        channel: NotificationChannelAPI.SMS,
        body: 'Great news! Your loan {{applicationId}} has been approved for ${{approvedAmount}}.',
      },
      {
        channel: NotificationChannelAPI.PUSH,
        title: 'Loan Approved! 🎉',
        body: 'Your loan for ${{approvedAmount}} has been approved!',
      },
    ],
    active: true,
  },
};

// =============================================================================
// NOTIFICATION RULE DATA
// =============================================================================

export interface NotificationRuleSeedData {
  id: string;
  businessId?: string;
  name: string;
  description?: string;
  active: boolean;
  event: string;
  conditions?: Record<string, unknown>;
  recipientType: 'ROLE' | 'ALL_BUSINESS_USERS' | 'SPECIFIC_USERS';
  recipientIds?: string[];
  roles?: string[];
  channels: string[];
  templateId?: string;
  throttle?: { perUser?: string; perEntity?: string };
  priority: number;
}

export const NOTIFICATION_RULES: Record<keyof typeof NOTIFICATION_RULE_IDS, NotificationRuleSeedData> = {
  // System-wide rules
  SYSTEM_CRITICAL_ALERT: {
    id: NOTIFICATION_RULE_IDS.SYSTEM_CRITICAL_ALERT,
    name: 'System Critical Alert',
    description: 'Notify all business admins on critical system events',
    active: true,
    event: 'CUSTOM',
    recipientType: 'ROLE',
    roles: ['business_admin'],
    channels: ['EMAIL', 'SMS', 'PUSH', 'IN_APP'],
    priority: 100,
  },
  SYSTEM_MAINTENANCE: {
    id: NOTIFICATION_RULE_IDS.SYSTEM_MAINTENANCE,
    name: 'Scheduled Maintenance',
    description: 'Notify all users of scheduled maintenance windows',
    active: true,
    event: 'CUSTOM',
    recipientType: 'ALL_BUSINESS_USERS',
    channels: ['EMAIL', 'IN_APP'],
    throttle: { perUser: '24h' },
    priority: 50,
  },

  // Business-level rules
  CASE_ASSIGNED_RULE: {
    id: NOTIFICATION_RULE_IDS.CASE_ASSIGNED_RULE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Assignment Notification',
    description: 'Notify user when a case is assigned to them',
    active: true,
    event: 'FIELD_CHANGED',
    recipientType: 'SPECIFIC_USERS',
    channels: ['EMAIL', 'IN_APP'],
    templateId: TEMPLATE_IDS.CASE_ASSIGNED,
    priority: 10,
  },
  LOAN_STATUS_CHANGE_RULE: {
    id: NOTIFICATION_RULE_IDS.LOAN_STATUS_CHANGE_RULE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Status Change Notification',
    description: 'Notify loan officers and borrowers of status changes',
    active: true,
    event: 'STATUS_CHANGED',
    conditions: { status: { $in: ['approved', 'rejected', 'funded'] } },
    recipientType: 'ROLE',
    roles: ['loan_officer', 'processor'],
    channels: ['EMAIL', 'SMS', 'IN_APP'],
    templateId: TEMPLATE_IDS.LOAN_STATUS_UPDATE,
    priority: 20,
  },
  DOCUMENT_DEADLINE_RULE: {
    id: NOTIFICATION_RULE_IDS.DOCUMENT_DEADLINE_RULE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Deadline Reminder',
    description: 'Remind users of upcoming document deadlines',
    active: true,
    event: 'CUSTOM',
    conditions: { daysUntilDeadline: { $lte: 3 } },
    recipientType: 'SPECIFIC_USERS',
    channels: ['EMAIL', 'IN_APP'],
    templateId: TEMPLATE_IDS.DOCUMENT_REQUESTED,
    throttle: { perEntity: '24h' },
    priority: 15,
  },
  PAYMENT_RECEIVED_RULE: {
    id: NOTIFICATION_RULE_IDS.PAYMENT_RECEIVED_RULE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Payment Received Notification',
    description: 'Notify business admins when payment is received',
    active: true,
    event: 'CREATED',
    recipientType: 'ROLE',
    roles: ['business_admin'],
    channels: ['EMAIL', 'IN_APP'],
    priority: 10,
  },
  SLA_BREACH_RULE: {
    id: NOTIFICATION_RULE_IDS.SLA_BREACH_RULE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'SLA Breach Alert',
    description: 'Alert managers when SLA is about to be breached',
    active: true,
    event: 'CUSTOM',
    conditions: { breachIn: { $lte: '1h' } },
    recipientType: 'ROLE',
    roles: ['manager', 'admin'],
    channels: ['EMAIL', 'SMS', 'PUSH'],
    priority: 80,
  },
};

// =============================================================================
// NOTIFICATION PREFERENCE DATA
// =============================================================================

export interface NotificationPreferenceSeedData {
  id: string;
  userId: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  inAppEnabled: boolean;
  eventPreferences?: Record<string, boolean>;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone: string;
  digestMode: boolean;
  digestFrequency?: 'daily' | 'weekly';
}

export const NOTIFICATION_PREFERENCES: Record<
  keyof typeof NOTIFICATION_PREFERENCE_IDS,
  NotificationPreferenceSeedData
> = {
  PREF_RACHEL: {
    id: NOTIFICATION_PREFERENCE_IDS.PREF_RACHEL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: true,
    inAppEnabled: true,
    eventPreferences: {
      'case.created': true,
      'case.assigned': true,
      'loan.status_changed': true,
      'system.maintenance': false,
    },
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    timezone: 'America/Chicago',
    digestMode: false,
  },
  PREF_MIKE: {
    id: NOTIFICATION_PREFERENCE_IDS.PREF_MIKE,
    userId: USER_IDS.MIKE_CHEN,
    emailEnabled: true,
    smsEnabled: false,
    pushEnabled: true,
    inAppEnabled: true,
    eventPreferences: {
      'case.created': true,
      'loan.status_changed': true,
    },
    timezone: 'America/Chicago',
    digestMode: true,
    digestFrequency: 'daily',
  },
  PREF_SARAH: {
    id: NOTIFICATION_PREFERENCE_IDS.PREF_SARAH,
    userId: USER_IDS.SARAH_WILLIAMS,
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: false,
    inAppEnabled: true,
    quietHoursStart: '21:00',
    quietHoursEnd: '08:00',
    timezone: 'America/New_York',
    digestMode: false,
  },
  PREF_SUPER_ADMIN: {
    id: NOTIFICATION_PREFERENCE_IDS.PREF_SUPER_ADMIN,
    userId: USER_IDS.SUPER_ADMIN,
    emailEnabled: true,
    smsEnabled: true,
    pushEnabled: true,
    inAppEnabled: true,
    eventPreferences: {
      'system.critical': true,
      'system.maintenance': true,
      'sla.breach_warning': true,
    },
    timezone: 'UTC',
    digestMode: false,
  },
};

// =============================================================================
// NOTIFICATION TRIGGER DATA
// =============================================================================

export interface NotificationTriggerSeedData {
  id: string;
  businessId: string | null;
  name: string;
  description?: string;
  entityType: NotificationEntityType;
  eventType: 'CREATED' | 'UPDATED' | 'FIELD_CHANGED' | 'STATUS_CHANGED' | 'DELETED' | 'CUSTOM';
  conditions?: Array<{ field: string; operator: string; value: string }>;
  templateId: string;
  recipientType: 'RECORD_OWNER' | 'ASSIGNED_USER' | 'FIELD_VALUE' | 'ROLE' | 'SPECIFIC_USERS' | 'ALL_BUSINESS_USERS';
  recipientConfig?: Record<string, unknown>;
  channels: Array<'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP'>;
  delaySeconds?: number;
  throttlePerUser?: string;
  throttlePerEntity?: string;
  active: boolean;
  priority: number;
}

export const NOTIFICATION_TRIGGERS: Record<keyof typeof NOTIFICATION_TRIGGER_IDS, NotificationTriggerSeedData> = {
  // Case triggers
  TRIGGER_CASE_CREATED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_CASE_CREATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Created Trigger',
    description: 'Send notification when a new case is created',
    entityType: NotificationEntityType.CASE,
    eventType: 'CREATED',
    templateId: TEMPLATE_IDS.CASE_CREATED,
    recipientType: 'ROLE',
    recipientConfig: { roles: ['case_manager', 'admin'] },
    channels: ['EMAIL', 'IN_APP'],
    active: true,
    priority: 10,
  },
  TRIGGER_CASE_ASSIGNED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_CASE_ASSIGNED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Assigned Trigger',
    description: 'Notify user when a case is assigned to them',
    entityType: NotificationEntityType.CASE,
    eventType: 'FIELD_CHANGED',
    conditions: [{ field: 'assigneeId', operator: 'changed', value: '*' }],
    templateId: TEMPLATE_IDS.CASE_ASSIGNED,
    recipientType: 'ASSIGNED_USER',
    channels: ['EMAIL', 'PUSH', 'IN_APP'],
    active: true,
    priority: 15,
  },
  TRIGGER_CASE_STATUS_CHANGED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_CASE_STATUS_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Case Status Changed Trigger',
    description: 'Notify on case status changes',
    entityType: NotificationEntityType.CASE,
    eventType: 'STATUS_CHANGED',
    templateId: TEMPLATE_IDS.CASE_STATUS_CHANGED,
    recipientType: 'RECORD_OWNER',
    channels: ['EMAIL', 'IN_APP'],
    throttlePerEntity: '1h',
    active: true,
    priority: 10,
  },
  TRIGGER_BORROWER_MESSAGE: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_BORROWER_MESSAGE,
    businessId: null, // System-wide — applies to all businesses
    name: 'Borrower Message Trigger',
    description: 'Notify case assignee when borrower sends a message',
    entityType: NotificationEntityType.CASE,
    eventType: 'UPDATED',
    conditions: [{ field: 'activity_type', operator: 'eq', value: 'borrower_message' }],
    templateId: TEMPLATE_IDS.BORROWER_MESSAGE,
    recipientType: 'ASSIGNED_USER',
    channels: ['EMAIL', 'PUSH', 'IN_APP'],
    throttlePerUser: '5m', // Don't spam for rapid messages
    active: true,
    priority: 20,
  },
  TRIGGER_BORROWER_QUOTE_ACCEPTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_BORROWER_QUOTE_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Borrower Quote Accepted Trigger',
    description: 'Notify case assignee when borrower accepts a quote',
    entityType: NotificationEntityType.CASE,
    eventType: 'UPDATED',
    conditions: [{ field: 'activity_type', operator: 'eq', value: 'borrower_quote_accepted' }],
    templateId: TEMPLATE_IDS.BORROWER_QUOTE_ACCEPTED,
    recipientType: 'ASSIGNED_USER',
    channels: ['EMAIL', 'PUSH', 'IN_APP'],
    active: true,
    priority: 25, // Higher priority - this is a key milestone
  },
  TRIGGER_LENDER_MESSAGE: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_LENDER_MESSAGE,
    businessId: null, // System-wide — applies to all businesses
    name: 'Lender Message Trigger',
    description: 'Notify borrower when lender sends a public message on their loan',
    entityType: NotificationEntityType.CASE,
    eventType: 'UPDATED',
    conditions: [{ field: 'activity_type', operator: 'eq', value: ACTIVITY_TYPE_LENDER_MESSAGE }],
    templateId: TEMPLATE_IDS.LENDER_MESSAGE,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'borrowerEmail' },
    channels: ['EMAIL', 'PUSH', 'IN_APP'],
    throttlePerUser: '5m',
    active: true,
    priority: 20,
  },

  // Loan triggers
  TRIGGER_LOAN_SUBMITTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_LOAN_SUBMITTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Submitted Trigger',
    description: 'Confirm loan application submission to borrower',
    entityType: NotificationEntityType.LOAN_APPLICATION,
    eventType: 'STATUS_CHANGED',
    conditions: [{ field: 'status', operator: 'eq', value: 'submitted' }],
    templateId: TEMPLATE_IDS.LOAN_APPLICATION_RECEIVED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'borrowerEmail' },
    channels: ['EMAIL', 'SMS'],
    active: true,
    priority: 20,
  },
  TRIGGER_LOAN_APPROVED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_LOAN_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Approved Trigger',
    description: 'Congratulate borrower on loan approval',
    entityType: NotificationEntityType.LOAN_APPLICATION,
    eventType: 'STATUS_CHANGED',
    conditions: [{ field: 'status', operator: 'eq', value: 'approved' }],
    templateId: TEMPLATE_IDS.LOAN_APPROVAL,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'borrowerEmail' },
    channels: ['EMAIL', 'SMS', 'PUSH'],
    active: true,
    priority: 25,
  },
  TRIGGER_LOAN_REJECTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_LOAN_REJECTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Loan Rejected Trigger',
    description: 'Notify borrower of loan rejection',
    entityType: NotificationEntityType.LOAN_APPLICATION,
    eventType: 'STATUS_CHANGED',
    conditions: [{ field: 'status', operator: 'eq', value: 'rejected' }],
    templateId: TEMPLATE_IDS.LOAN_STATUS_UPDATE,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'borrowerEmail' },
    channels: ['EMAIL'],
    delaySeconds: 300, // 5 minute delay for review
    active: true,
    priority: 20,
  },

  // Document triggers
  TRIGGER_DOC_REQUESTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_DOC_REQUESTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Requested Trigger',
    description: 'Notify consumer when document is requested',
    entityType: NotificationEntityType.DOCUMENT,
    eventType: 'CREATED',
    templateId: TEMPLATE_IDS.DOCUMENT_REQUESTED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'consumerEmail' },
    channels: ['EMAIL', 'SMS'],
    active: true,
    priority: 15,
  },
  TRIGGER_DOC_UPLOADED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_DOC_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Uploaded Trigger',
    description: 'Notify loan officer when document is uploaded',
    entityType: NotificationEntityType.DOCUMENT,
    eventType: 'CREATED',
    templateId: TEMPLATE_IDS.DOCUMENT_UPLOADED,
    recipientType: 'ASSIGNED_USER',
    recipientConfig: { relation: 'case.assignee' },
    channels: ['IN_APP'],
    active: true,
    priority: 5,
  },
  TRIGGER_DOC_EXPIRING: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_DOC_EXPIRING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Document Expiring Trigger',
    description: 'Notify when document is about to expire',
    entityType: NotificationEntityType.DOCUMENT,
    eventType: 'CUSTOM',
    conditions: [{ field: 'expiresAt', operator: 'within_days', value: '30' }],
    templateId: TEMPLATE_IDS.DOCUMENT_REQUESTED,
    recipientType: 'RECORD_OWNER',
    channels: ['EMAIL', 'IN_APP'],
    throttlePerEntity: '7d',
    active: true,
    priority: 10,
  },

  // Auth / User triggers (Issue #699)
  TRIGGER_WELCOME_EMAIL: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_WELCOME_EMAIL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Welcome Email Trigger',
    description: 'Send welcome email when a new user registers',
    entityType: NotificationEntityType.USER,
    eventType: 'CREATED',
    templateId: TEMPLATE_IDS.WELCOME_EMAIL,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 20,
  },
  TRIGGER_PASSWORD_CHANGED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_PASSWORD_CHANGED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Password Changed Alert',
    description: 'Security alert when user changes password',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'password_changed' }],
    templateId: TEMPLATE_IDS.PASSWORD_CHANGED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 30,
  },
  TRIGGER_EMPLOYEE_INVITE_ACCEPTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_EMPLOYEE_INVITE_ACCEPTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Employee Invite Accepted',
    description: 'Notify admins when employee accepts invitation',
    entityType: NotificationEntityType.USER,
    eventType: 'STATUS_CHANGED',
    conditions: [{ field: 'status', operator: 'eq', value: 'accepted' }],
    templateId: TEMPLATE_IDS.EMPLOYEE_INVITE_ACCEPTED,
    recipientType: 'ROLE',
    recipientConfig: { roles: ['admin'] },
    channels: ['EMAIL', 'IN_APP'],
    active: true,
    priority: 10,
  },
  TRIGGER_CONSUMER_INVITED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_CONSUMER_INVITED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Consumer Invitation Trigger',
    description: 'Send invitation email when consumer is invited',
    entityType: NotificationEntityType.USER,
    eventType: 'CREATED',
    templateId: TEMPLATE_IDS.CONSUMER_INVITED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'consumerEmail' },
    channels: ['EMAIL'],
    active: true,
    priority: 20,
  },
  TRIGGER_MFA_ENABLED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_MFA_ENABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'MFA Enabled Confirmation',
    description: 'Confirm MFA enrollment to user',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'mfa_enabled' }],
    templateId: TEMPLATE_IDS.MFA_ENABLED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 20,
  },
  TRIGGER_MFA_DISABLED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_MFA_DISABLED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'MFA Disabled Alert',
    description: 'Security alert when MFA is disabled',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'mfa_disabled' }],
    templateId: TEMPLATE_IDS.MFA_DISABLED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 30,
  },
  TRIGGER_NEW_DEVICE_LOGIN: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_NEW_DEVICE_LOGIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'New Device Login Alert',
    description: 'Security alert for logins from unrecognized devices',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'new_device_login' }],
    templateId: TEMPLATE_IDS.NEW_DEVICE_LOGIN,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    throttlePerUser: '1h',
    active: true,
    priority: 25,
  },
  TRIGGER_ACCOUNT_DEACTIVATED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_ACCOUNT_DEACTIVATED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Account Deactivated Notice',
    description: 'Notify user when their account is deactivated',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'deactivated' }],
    templateId: TEMPLATE_IDS.ACCOUNT_DEACTIVATED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 30,
  },

  // Domain Intelligence triggers (Issue #703)
  TRIGGER_DOMAIN_MATCH_DETECTED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_DOMAIN_MATCH_DETECTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Domain Match Detected',
    description: 'Notify business admins when someone from their domain tries to sign up',
    entityType: NotificationEntityType.BUSINESS,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'domain_match_detected' }],
    templateId: TEMPLATE_IDS.DOMAIN_MATCH_DETECTED,
    recipientType: 'ROLE',
    recipientConfig: { roles: ['admin'] },
    channels: ['EMAIL', 'IN_APP'],
    active: true,
    priority: 20,
  },
  TRIGGER_PARTNER_EXISTING_BUSINESS: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_PARTNER_EXISTING_BUSINESS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Partner Existing Business',
    description: 'Notify B2 admin when partner links to their existing business',
    entityType: NotificationEntityType.BUSINESS,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'partner_existing_business' }],
    templateId: TEMPLATE_IDS.PARTNER_EXISTING_BUSINESS,
    recipientType: 'ROLE',
    recipientConfig: { roles: ['admin'] },
    channels: ['EMAIL', 'IN_APP'],
    active: true,
    priority: 15,
  },
  TRIGGER_BUSINESS_OWNER_INVITED: {
    id: NOTIFICATION_TRIGGER_IDS.TRIGGER_BUSINESS_OWNER_INVITED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Business Owner Invited',
    description: 'Send invitation to business owner when platform admin creates their account',
    entityType: NotificationEntityType.USER,
    eventType: 'CUSTOM',
    conditions: [{ field: 'customEventType', operator: 'eq', value: 'business_owner_invited' }],
    templateId: TEMPLATE_IDS.BUSINESS_OWNER_INVITED,
    recipientType: 'FIELD_VALUE',
    recipientConfig: { field: 'email' },
    channels: ['EMAIL'],
    active: true,
    priority: 25,
  },
};

// =============================================================================
// NOTIFICATION PROVIDER DATA
// =============================================================================

export interface NotificationProviderSeedData {
  id: string;
  businessId: string;
  channel: 'EMAIL' | 'SMS' | 'PUSH' | 'IN_APP';
  provider: string;
  config: {
    apiKeyRef?: string;
    fromEmail?: string;
    fromName?: string;
    fromPhone?: string;
    projectId?: string;
  };
  isDefault: boolean;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
}

export const NOTIFICATION_PROVIDERS: Record<keyof typeof NOTIFICATION_PROVIDER_IDS, NotificationProviderSeedData> = {
  // OpsMatrix Capital providers
  OPSMATRIX_EMAIL_RESEND: {
    id: NOTIFICATION_PROVIDER_IDS.OPSMATRIX_EMAIL_RESEND,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    channel: 'EMAIL',
    provider: 'resend',
    config: {
      apiKeyRef: 'secrets/resend/opsmatrix',
      fromEmail: 'notifications@opsmatrix.local',
      fromName: 'Fiops Capital',
    },
    isDefault: true,
    status: 'ACTIVE',
  },
  OPSMATRIX_SMS_TWILIO: {
    id: NOTIFICATION_PROVIDER_IDS.OPSMATRIX_SMS_TWILIO,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    channel: 'SMS',
    provider: 'twilio',
    config: {
      apiKeyRef: 'secrets/twilio/opsmatrix',
      fromPhone: '+12145551000',
    },
    isDefault: true,
    status: 'ACTIVE',
  },
  OPSMATRIX_PUSH_FIREBASE: {
    id: NOTIFICATION_PROVIDER_IDS.OPSMATRIX_PUSH_FIREBASE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    channel: 'PUSH',
    provider: 'firebase',
    config: {
      projectId: 'opsmatrix-prod',
      apiKeyRef: 'secrets/firebase/opsmatrix',
    },
    isDefault: true,
    status: 'ACTIVE',
  },

  // 1Sharpe Capital providers
  SHARPE_EMAIL_RESEND: {
    id: NOTIFICATION_PROVIDER_IDS.SHARPE_EMAIL_RESEND,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    channel: 'EMAIL',
    provider: 'resend',
    config: {
      apiKeyRef: 'secrets/resend/sharpe',
      fromEmail: 'notifications@1sharpe.local',
      fromName: '1Sharpe Capital',
    },
    isDefault: true,
    status: 'ACTIVE',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all notification templates as an array
 */
export function getAllNotificationTemplates(): NotificationTemplateSeedData[] {
  return Object.values(NOTIFICATION_TEMPLATES);
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: string): NotificationTemplateSeedData[] {
  return Object.values(NOTIFICATION_TEMPLATES).filter((t) => t.category === category);
}

/**
 * Get templates by business ID
 */
export function getTemplatesByBusiness(businessId: string): NotificationTemplateSeedData[] {
  return Object.values(NOTIFICATION_TEMPLATES).filter((t) => t.businessId === businessId);
}

/**
 * Get template by ID
 */
export function getTemplateById(templateId: string): NotificationTemplateSeedData | undefined {
  return Object.values(NOTIFICATION_TEMPLATES).find((t) => t.id === templateId);
}

/**
 * Get all notification rules as an array
 */
export function getAllNotificationRules(): NotificationRuleSeedData[] {
  return Object.values(NOTIFICATION_RULES);
}

/**
 * Get notification rules by business ID
 */
export function getNotificationRulesByBusiness(businessId: string): NotificationRuleSeedData[] {
  return Object.values(NOTIFICATION_RULES).filter((r) => r.businessId === businessId);
}

/**
 * Get active notification rules
 */
export function getActiveNotificationRules(): NotificationRuleSeedData[] {
  return Object.values(NOTIFICATION_RULES).filter((r) => r.active);
}

/**
 * Get all notification preferences as an array
 */
export function getAllNotificationPreferences(): NotificationPreferenceSeedData[] {
  return Object.values(NOTIFICATION_PREFERENCES);
}

/**
 * Get notification preference by user ID
 */
export function getNotificationPreferenceByUser(userId: string): NotificationPreferenceSeedData | undefined {
  return Object.values(NOTIFICATION_PREFERENCES).find((p) => p.userId === userId);
}

/**
 * Get all notification triggers as an array
 */
export function getAllNotificationTriggers(): NotificationTriggerSeedData[] {
  return Object.values(NOTIFICATION_TRIGGERS);
}

/**
 * Get notification triggers by business ID
 */
export function getNotificationTriggersByBusiness(businessId: string): NotificationTriggerSeedData[] {
  return Object.values(NOTIFICATION_TRIGGERS).filter((t) => t.businessId === businessId);
}

/**
 * Get notification triggers by entity type
 */
export function getNotificationTriggersByEntity(entityType: NotificationEntityType): NotificationTriggerSeedData[] {
  return Object.values(NOTIFICATION_TRIGGERS).filter((t) => t.entityType === entityType);
}

/**
 * Get all notification providers as an array
 */
export function getAllNotificationProviders(): NotificationProviderSeedData[] {
  return Object.values(NOTIFICATION_PROVIDERS);
}

/**
 * Get notification providers by business ID
 */
export function getNotificationProvidersByBusiness(businessId: string): NotificationProviderSeedData[] {
  return Object.values(NOTIFICATION_PROVIDERS).filter((p) => p.businessId === businessId);
}

/**
 * Get default provider for a channel and business
 */
export function getDefaultProvider(
  businessId: string,
  channel: NotificationProviderSeedData['channel'],
): NotificationProviderSeedData | undefined {
  return Object.values(NOTIFICATION_PROVIDERS).find(
    (p) => p.businessId === businessId && p.channel === channel && p.isDefault,
  );
}

// =============================================================================
// NOTIFICATION RECORD IDs (Issue #1269 — Inbox seed data)
// =============================================================================

export const NOTIFICATION_RECORD_IDS = {
  // Rachel Martinez — 7 notifications
  RACHEL_DOC_UPLOADED: seedId('notif-rachel-doc-upload', 1),
  RACHEL_CASE_ASSIGNED: seedId('notif-rachel-case-assign', 2),
  RACHEL_SYSTEM_UPDATE: seedId('notif-rachel-sys-update', 3),
  RACHEL_BILLING_INVOICE: seedId('notif-rachel-billing', 4),
  RACHEL_SECURITY_ALERT: seedId('notif-rachel-security', 5),
  RACHEL_AGENT_INSIGHT: seedId('notif-rachel-agent', 6),
  RACHEL_BORROWER_MSG: seedId('notif-rachel-borrower-msg', 7),
  // Mike Johnson — 3 notifications
  MIKE_CASE_STATUS: seedId('notif-mike-case-status', 8),
  MIKE_DOC_REQUEST: seedId('notif-mike-doc-request', 9),
  MIKE_SYSTEM_MAINT: seedId('notif-mike-sys-maint', 10),
  // Sarah Chen — 2 notifications
  SARAH_NEW_CASE: seedId('notif-sarah-new-case', 11),
  SARAH_USER_JOINED: seedId('notif-sarah-user-joined', 12),
};

// =============================================================================
// NOTIFICATION RECORD SEED DATA (Issue #1269)
// =============================================================================

export interface NotificationRecordSeedData {
  id: string;
  businessId: string;
  userId: string; // Seed-time placeholder ID (USER_IDS.*) — replaced with real Firebase UID via userEmail lookup at seed time
  userEmail: string; // Canonical user lookup key — notification seeder resolves to real userId from account-service DB
  type: string;
  channel: NotificationChannelAPI;
  title: string;
  message: string;
  event: string;
  severity: string;
  status: 'PENDING' | 'READ';
  actionUrl?: string;
  imageUrl?: string;
}

/**
 * 12 notification records for inbox demo:
 * - 7 for Rachel (4 PENDING/unread, 3 READ)
 * - 3 for Mike (2 PENDING, 1 READ)
 * - 2 for Sarah (1 PENDING, 1 READ)
 */
export const NOTIFICATION_RECORDS: NotificationRecordSeedData[] = [
  // --- Rachel Martinez (7 notifications) ---
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_DOC_UPLOADED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'document',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Document uploaded: W-2 Tax Form',
    message: 'Marcus Thompson uploaded a W-2 Tax Form for loan application LN-2026-001234.',
    event: 'document.uploaded',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/loans/applications',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_CASE_ASSIGNED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'New case assigned: Bridge Loan #BL-4021',
    message: 'You have been assigned to a new bridge loan case for Apex Development LLC.',
    event: 'case.assigned',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/cases',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_SYSTEM_UPDATE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Platform update: New compliance dashboard',
    message:
      'A new compliance monitoring dashboard is now available under Reports. Check it out to track regulatory requirements.',
    event: 'system.update',
    severity: 'info',
    status: 'READ',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_BILLING_INVOICE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'billing',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Invoice #INV-2026-0042 generated',
    message: 'Your monthly platform usage invoice for $1,249.00 is ready for review.',
    event: 'billing.invoice_created',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/control-center',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_SECURITY_ALERT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Security: New device login detected',
    message:
      'A new login was detected from Chrome on macOS in Austin, TX. If this was not you, please secure your account immediately.',
    event: 'security.new_device',
    severity: 'warning',
    status: 'PENDING',
    actionUrl: '/control-center',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_AGENT_INSIGHT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'agent',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Astor insight: Portfolio risk assessment complete',
    message:
      'AI analysis of your active portfolio identified 2 loans approaching covenant thresholds. Review recommended.',
    event: 'agent.insight',
    severity: 'warning',
    status: 'READ',
  },
  {
    id: NOTIFICATION_RECORD_IDS.RACHEL_BORROWER_MSG,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.RACHEL_MARTINEZ,
    userEmail: 'rachel.martinez@opsmatrix.local',
    type: 'user',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Message from borrower: Marcus Thompson',
    message:
      'Hi Rachel, I wanted to follow up on the appraisal timeline for my refinance application. When can I expect the report?',
    event: 'case.borrower_message',
    severity: 'info',
    status: 'READ',
    actionUrl: '/cases',
  },

  // --- Mike Johnson (3 notifications) ---
  {
    id: NOTIFICATION_RECORD_IDS.MIKE_CASE_STATUS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_JOHNSON,
    userEmail: 'mike.johnson@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Case status changed: Construction Loan #CL-3887',
    message:
      'The construction loan case for Riverside Properties has moved from "Underwriting" to "Approved with Conditions".',
    event: 'case.status_changed',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/cases',
  },
  {
    id: NOTIFICATION_RECORD_IDS.MIKE_DOC_REQUEST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_JOHNSON,
    userEmail: 'mike.johnson@opsmatrix.local',
    type: 'document',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Document request: Environmental Phase I',
    message: 'An Environmental Phase I report has been requested for the Riverside Properties construction loan.',
    event: 'document.requested',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/documents',
  },
  {
    id: NOTIFICATION_RECORD_IDS.MIKE_SYSTEM_MAINT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.MIKE_JOHNSON,
    userEmail: 'mike.johnson@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'Scheduled maintenance: April 20, 2:00 AM CT',
    message:
      'The platform will undergo scheduled maintenance on April 20, 2026 from 2:00 AM to 4:00 AM CT. Service may be briefly unavailable.',
    event: 'system.maintenance',
    severity: 'info',
    status: 'READ',
  },

  // --- Sarah Chen (2 notifications) ---
  {
    id: NOTIFICATION_RECORD_IDS.SARAH_NEW_CASE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.SARAH_CHEN,
    userEmail: 'sarah.chen@opsmatrix.local',
    type: 'system',
    channel: NotificationChannelAPI.IN_APP,
    title: 'New case created: Fix & Flip #FF-2190',
    message: 'A new fix & flip case has been created for Johnson Development Group. Initial loan amount: $425,000.',
    event: 'case.created',
    severity: 'info',
    status: 'PENDING',
    actionUrl: '/cases',
  },
  {
    id: NOTIFICATION_RECORD_IDS.SARAH_USER_JOINED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    userId: USER_IDS.SARAH_CHEN,
    userEmail: 'sarah.chen@opsmatrix.local',
    type: 'user',
    channel: NotificationChannelAPI.IN_APP,
    title: 'New team member: David Park joined',
    message: 'David Park has accepted the invitation and joined the team as a Loan Officer.',
    event: 'user.created',
    severity: 'info',
    status: 'READ',
  },
];

/**
 * Get all notification records as an array
 */
export function getAllNotificationRecords(): NotificationRecordSeedData[] {
  return NOTIFICATION_RECORDS;
}

/**
 * Get notification records matching a seed-time placeholder user ID (USER_IDS.*).
 *
 * Note: These records' `userId` fields are deterministic seed placeholders, not the
 * final Firebase UIDs persisted by Account Service. Use `getNotificationRecordsByUserEmail()`
 * when you need to match records against real users during seeding.
 */
export function getNotificationRecordsBySeedUserId(userId: string): NotificationRecordSeedData[] {
  return NOTIFICATION_RECORDS.filter((n) => n.userId === userId);
}

/**
 * Get notification records for a user email. `userEmail` is the canonical, stable
 * lookup key — prefer this helper when resolving records during seeding.
 */
export function getNotificationRecordsByUserEmail(userEmail: string): NotificationRecordSeedData[] {
  return NOTIFICATION_RECORDS.filter((n) => n.userEmail === userEmail);
}
